import { useCallback, useRef, useState, type ReactNode } from 'react';
import type { PedirCodigo } from '../api/dosPasosEnElMomento';
import type { RespuestaDosPasosEnElMomento } from '../api/operaciones';
import { Campo } from '../componentes/Campo';
import { Ventana } from '../componentes/Ventana';
import { MENSAJES, validarSegundoPaso } from '../logica/acceso';
import { ContextoDosPasos as Contexto } from './dosPasos';

/**
 * Ventana del código de dos pasos en el momento (G116; texto aprobado en G123; dibujo de 1.19 «Confirma que eres tú»):
 * RV Ventana con RV Campo de 6 cifras, «Usar un código de recuperación», «Confirmar» y «Cancelar». La abre
 * `conDosPasosEnElMomento` (api/dosPasosEnElMomento.ts) con `usePedirCodigoDosPasos()`.
 */

export function ProveedorDosPasos({ movil, children }: { movil: boolean; children: ReactNode }) {
    const [abierta, setAbierta] = useState(false);
    const [recuperacion, setRecuperacion] = useState(false);
    const [valor, setValor] = useState('');
    const [error, setError] = useState('');
    const resolver = useRef<((r: RespuestaDosPasosEnElMomento | null) => void) | null>(null);

    const pedir = useCallback<PedirCodigo>((fallido) => {
        setAbierta(true);
        setValor('');
        setError(fallido ? MENSAJES.codigoIncorrecto : '');
        return new Promise((r) => {
            resolver.current = r;
        });
    }, []);

    const termina = (respuesta: RespuestaDosPasosEnElMomento | null) => {
        setAbierta(false);
        resolver.current?.(respuesta);
        resolver.current = null;
    };

    const confirmar = () => {
        const fallo = validarSegundoPaso(recuperacion, valor);
        if (fallo !== null) {
            setError(fallo);
            return;
        }
        termina(recuperacion ? { recovery_code: valor.trim() } : { two_factor_code: valor });
    };

    return (
        <Contexto.Provider value={pedir}>
            {children}
            <Ventana
                abierta={abierta}
                titulo="Confirma que eres tú"
                texto={MENSAJES.codigoEnElMomento}
                accion="Confirmar"
                accionIcono="shield-check"
                movil={movil}
                onAccion={confirmar}
                onCerrar={() => {
                    termina(null);
                }}
            >
                <Campo
                    etiqueta={recuperacion ? 'Código de recuperación' : 'Código de 6 números'}
                    valor={valor}
                    modo={recuperacion ? 'text' : 'numeric'}
                    autocompletar="one-time-code"
                    max={recuperacion ? 9 : 6}
                    error={error}
                    onEnter={confirmar}
                    onChange={(v) => {
                        setValor(recuperacion ? v.toUpperCase().slice(0, 9) : v.replace(/\D/g, '').slice(0, 6));
                        setError('');
                    }}
                />
                <button
                    type="button"
                    className="rv-acceso__enlace"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => {
                        setRecuperacion((r) => !r);
                        setValor('');
                        setError('');
                    }}
                >
                    {recuperacion ? 'Usar la aplicación' : 'Usar un código de recuperación'}
                </button>
            </Ventana>
        </Contexto.Provider>
    );
}
